/**
 * Event/phase times are stored as wall-clock times on the event's date: the
 * HH:MM lives directly in the ISO string (UTC), and we never timezone-convert —
 * a time picker shows exactly what was stored. These helpers move between the
 * stored ISO, a 24h `HH:MM` input value, and a 12h display label.
 *
 * The event's *date* is a different kind of thing — a calendar day — and
 * `formatDate` is what knows the difference. See `docs/event-scheduling.md`.
 */
import { formatDate } from '@lib/helpers/format'
import { toTimeOfDay, twelveHourParts, type TimeOfDay } from '@lib/validators/time-of-day.validator'
import {
  calendarDay,
  wallClock,
  type CalendarDay,
  type WallClock,
} from '@lib/validators/temporal.validator'

/** ISO datetime → `HH:MM` (24h) for a time input; '' when absent or not a time. */
export function toTimeInput(iso?: WallClock | null): TimeOfDay | '' {
  if (iso === null || iso === undefined) return ''
  return toTimeOfDay(iso.slice(11, 16)) ?? ''
}

/**
 * `HH:MM` + a calendar day → the wall clock on that day.
 *
 * Null when either is missing. The clock is already a {@link TimeOfDay}, so a
 * value like `99:99` cannot reach here — that rejection happens where the
 * string arrives, in {@link toTimeOfDay}.
 */
export function toIsoTime(date: CalendarDay | null, hhmm: TimeOfDay | ''): WallClock | null {
  if (date === null || hhmm === '') return null
  return wallClock(`${date}T${hhmm}:00.000Z`)
}

const pad = (n: number): string => String(n).padStart(2, '0')

/** Stored wall-clock time → 12h label (e.g. "3:00 PM"); '' when absent/invalid. */
export function fmt12(iso?: WallClock | null): string {
  const hhmm = toTimeInput(iso)
  if (hhmm === '') return ''
  const { hour12, minute, pm } = twelveHourParts(hhmm)
  return `${String(hour12)}:${pad(minute)} ${pm ? 'PM' : 'AM'}`
}

/**
 * Stored wall-clock time → compact 12h label (`10p`, `6:30p`); '' when absent.
 *
 * For the calendar chips, where a cell is about 120px wide and "10:00 PM" spent
 * nearly all of it — the event's name, the thing being looked for, was reduced
 * to one letter. The hour is what a glance needs; the full time is in the
 * tooltip.
 *
 * Built from the stored `HH:MM` like {@link fmt12}, not from `getHours()`. A
 * `Date`'s local hours are the viewer's, and these times are the venue's.
 */
export function fmtCompact12(iso?: WallClock | null): string {
  const hhmm = toTimeInput(iso)
  if (hhmm === '') return ''
  const { hour12, minute, pm } = twelveHourParts(hhmm)
  const suffix = pm ? 'p' : 'a'
  return minute === 0 ? `${String(hour12)}${suffix}` : `${String(hour12)}:${pad(minute)}${suffix}`
}

/** Display range like "3:00 PM – 3:45 PM"; '' when untimed. */
/** `16:00` → "4:00 PM", as the time pickers show it. */
export function clockLabel(time: TimeOfDay): string {
  const { hour12, minute, pm } = twelveHourParts(time)
  return `${String(hour12)}:${String(minute).padStart(2, '0')} ${pm ? 'PM' : 'AM'}`
}

export function timeRangeLabel(startsAt?: WallClock | null, endsAt?: WallClock | null): string {
  const s = fmt12(startsAt)
  const e = fmt12(endsAt)
  if (s && e) return `${s} – ${e}`
  return s || e || ''
}

/**
 * Long date like "May 14, 2027"; em dash when undated.
 *
 * Delegates to the shared `formatDate`, which formats a `yyyy-MM-dd` in UTC and
 * a real instant in the viewer's zone. Formatting a day in the viewer's zone is
 * how a 14 May event showed as "May 13" in Los Angeles — the day anchor is not a
 * moment, so there is no zone to convert it into.
 */
export function formatEventDate(date: CalendarDay | null): string {
  if (date === null) return '—'
  if (Number.isNaN(new Date(date).getTime())) return '—'
  // An event's date is a day by definition, so anything longer is the legacy
  // midnight-timestamp shape and the time on it is noise. The DTO normalizes
  // this at the network boundary too; doing it here as well means the guarantee
  // does not depend on the caller having come through a parse.
  return formatDate(calendarDay(date))
}
